import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    CheckCircle, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
    Loader2, RefreshCw
} from 'lucide-react';
import { dataQualityService } from '../../services/dataQualityService';
import CustomSelect from '../shared/CustomSelect';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { ErrorAlert } from '../shared/ErrorAlert';

const CompletenessContent = () => {
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('RETAIL');
    const [clients, setClients] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [paginating, setPaginating] = useState(false);
    const [error, setError] = useState('');
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const fetchData = useCallback(async () => {
        try {
            setPaginating(true);
            setError('');

            const [completenessResponse, statsResponse] = await Promise.all([
                activeTab === 'RETAIL'
                    ? dataQualityService.completeness.getRetail(currentPage, pageSize)
                    : dataQualityService.completeness.getCorporate(currentPage, pageSize),
                dataQualityService.completeness.getStats(activeTab),
            ]);

            if (completenessResponse.success && completenessResponse.data) {
                const pageData = completenessResponse.data;
                setClients(pageData.content || []);
                setTotalElements(pageData.totalElements || 0);
                setTotalPages(pageData.totalPages || 0);
            }

            if (statsResponse.success && statsResponse.data) {
                setStats(statsResponse.data);
            }
        } catch (err) {
            console.error('Error fetching completeness data:', err);
            setError('Failed to load completeness data');
        } finally {
            setLoading(false);
            setPaginating(false);
        }
    }, [activeTab, currentPage, pageSize]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        setCurrentPage(0);
    }, [activeTab]);

    const getCompletenessColor = (pct) => {
        if (pct >= 80) return 'bg-green-500';
        if (pct >= 50) return 'bg-yellow-500';
        return 'bg-red-500';
    };

    const getCompletenessTextColor = (pct) => {
        if (pct >= 80) return 'text-green-700';
        if (pct >= 50) return 'text-yellow-700';
        return 'text-red-700';
    };

    if (loading) {
        return <LoadingSpinner message="Loading completeness data..." />;
    }

    const startIndex = Math.min(currentPage * pageSize + 1, totalElements);
    const endIndex = Math.min((currentPage + 1) * pageSize, totalElements);

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex justify-between items-center">
                <div>
                    <h2 className="text-2xl font-semibold text-gray-900">Profile Completeness</h2>
                    <p className="text-gray-600 mt-1">{totalElements.toLocaleString()} clients</p>
                </div>
                <button
                    onClick={fetchData}
                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition flex items-center gap-2"
                >
                    <RefreshCw className="w-4 h-4" />
                    Refresh
                </button>
            </div>

            {error && <ErrorAlert error={error} onRetry={fetchData} />}

            {/* Stats Cards */}
            {stats && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <p className="text-sm text-gray-500">Total Clients</p>
                        <p className="text-2xl font-bold text-gray-900">{(stats.totalClients || 0).toLocaleString()}</p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <p className="text-sm text-gray-500">Complete Profiles</p>
                        <p className="text-2xl font-bold text-green-600">{(stats.completeProfiles || 0).toLocaleString()}</p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <p className="text-sm text-gray-500">Incomplete Profiles</p>
                        <p className="text-2xl font-bold text-red-600">{(stats.incompleteProfiles || 0).toLocaleString()}</p>
                    </div>
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                        <p className="text-sm text-gray-500">Avg Completeness</p>
                        <p className="text-2xl font-bold text-gray-900">{Number(stats.averageCompleteness || 0).toFixed(1)}%</p>
                    </div>
                </div>
            )}

            {/* Tabs */}
            <div className="bg-white rounded-xl border border-gray-200 p-1 inline-flex">
                {['RETAIL', 'CORPORATE'].map((tab) => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`px-6 py-2 rounded-lg text-sm font-medium transition ${
                            activeTab === tab
                                ? 'bg-blue-600 text-white'
                                : 'text-gray-600 hover:bg-gray-50'
                        }`}
                    >
                        {tab}
                    </button>
                ))}
            </div>

            {/* Clients Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden relative">
                {paginating && (
                    <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    </div>
                )}

                {clients.length === 0 ? (
                    <div className="text-center py-12">
                        <CheckCircle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No clients found</p>
                    </div>
                ) : (
                    <table className="w-full">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field Score</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">KYC Score</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Overall</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fields</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                            {clients.map((client) => (
                                <tr
                                    key={client.clientId}
                                    className="hover:bg-gray-50 transition cursor-pointer"
                                    onClick={() => navigate(`/data-quality/completeness/client/${client.clientId}`)}
                                >
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <p className="text-sm font-medium text-gray-900">{client.clientName}</p>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {client.clientCode}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center gap-2">
                                            <div className="w-24 bg-gray-200 rounded-full h-2">
                                                <div
                                                    className={`h-2 rounded-full ${getCompletenessColor(client.fieldScore)}`}
                                                    style={{ width: `${Math.min(client.fieldScore, 100)}%` }}
                                                ></div>
                                            </div>
                                            <span className={`text-sm font-medium ${getCompletenessTextColor(client.fieldScore)}`}>
                                                {Number(client.fieldScore).toFixed(0)}%
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center gap-2">
                                            <div className="w-24 bg-gray-200 rounded-full h-2">
                                                <div
                                                    className={`h-2 rounded-full ${getCompletenessColor(client.kycScore)}`}
                                                    style={{ width: `${Math.min(client.kycScore, 100)}%` }}
                                                ></div>
                                            </div>
                                            <span className={`text-sm font-medium ${getCompletenessTextColor(client.kycScore)}`}>
                                                {Number(client.kycScore).toFixed(0)}%
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center gap-2">
                                            <div className="w-24 bg-gray-200 rounded-full h-2.5">
                                                <div
                                                    className={`h-2.5 rounded-full ${getCompletenessColor(client.completenessPercentage)}`}
                                                    style={{ width: `${Math.min(client.completenessPercentage, 100)}%` }}
                                                ></div>
                                            </div>
                                            <span className={`text-sm font-bold ${getCompletenessTextColor(client.completenessPercentage)}`}>
                                                {Number(client.completenessPercentage).toFixed(0)}%
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                        {client.completedFields}/{client.totalRequiredFields}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {client.complete ? (
                                            <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">Complete</span>
                                        ) : (
                                            <span className="px-2 py-1 text-xs rounded-full bg-yellow-100 text-yellow-800">Incomplete</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {/* Pagination */}
                {(totalPages > 0 || totalElements > 0) && (
                    <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-200 sm:px-6">
                        <div className="flex items-center gap-4">
                            <span className="text-sm text-gray-700">
                                Showing <span className="font-medium">{startIndex.toLocaleString()}</span> to{' '}
                                <span className="font-medium">{endIndex.toLocaleString()}</span> of{' '}
                                <span className="font-medium">{totalElements.toLocaleString()}</span> results
                            </span>
                            <CustomSelect
                                value={pageSize}
                                onChange={(val) => { setPageSize(Number(val)); setCurrentPage(0); }}
                                options={[
                                    { value: 10, label: '10 per page' },
                                    { value: 20, label: '20 per page' },
                                    { value: 50, label: '50 per page' },
                                ]}
                                dropUp
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={() => setCurrentPage(0)} disabled={currentPage === 0 || paginating} className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition" title="First page">
                                <ChevronsLeft className="w-5 h-5" />
                            </button>
                            <button onClick={() => setCurrentPage(currentPage - 1)} disabled={currentPage === 0 || paginating} className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition" title="Previous page">
                                <ChevronLeft className="w-5 h-5" />
                            </button>
                            <span className="px-4 py-2 text-sm text-gray-700">
                                Page {(currentPage + 1).toLocaleString()} of {totalPages.toLocaleString() || 1}
                            </span>
                            <button onClick={() => setCurrentPage(currentPage + 1)} disabled={currentPage >= totalPages - 1 || paginating} className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition" title="Next page">
                                <ChevronRight className="w-5 h-5" />
                            </button>
                            <button onClick={() => setCurrentPage(totalPages - 1)} disabled={currentPage >= totalPages - 1 || paginating} className="p-2 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition" title="Last page">
                                <ChevronsRight className="w-5 h-5" />
                            </button>
                            {paginating && <Loader2 className="w-5 h-5 animate-spin text-blue-600 ml-2" />}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CompletenessContent;
